import { motion } from "framer-motion";
import { type FC, useEffect, useRef } from "react";

export const BadRequestPage: FC<{ message: string }> = ({ message }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        width: "100%",
        height: "100%",
        background: "white",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        margin: "auto",
        maxWidth: 640,
      }}
      data-testid="loading-dna"
    >
      <Animation />
      <div
        style={{
          textAlign: "left",
          fontSize: 40,
          fontWeight: 350,
          lineHeight: 0.75,
          margin: 10,
        }}
      >
        Validation Error
      </div>
      <pre id="error-message" style={{ color: "var(--primary-main)" }} data-testid="error-message">
        {message.includes("<!DOCTYPE html>") ? "An unknown error occurred." : message}
      </pre>
    </motion.div>
  );
};

const Animation = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    let frame = 0;
    let animationFrameId: number;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw the DNA strands
      for (let i = 0; i < canvas.width; i += 4) {
        const baseY = canvas.height / 2;
        const amplitude = 30;
        const frequency = 0.02;
        const speed = 0.05;

        // Create smoother irregularities
        const irregularity = Math.sin(i * 0.05 + frame * 0.02) * Math.sin(frame * 0.01);

        // Calculate positions with gentler distortions
        const y1 = baseY + Math.sin(i * frequency + frame * speed) * amplitude * (1 + irregularity * 0.5);

        const y2 = baseY + Math.sin((i + 100) * frequency + frame * speed) * amplitude * (1 - irregularity * 0.5);

        // Color based on irregularity
        const errorIntensity = Math.abs(irregularity);

        ctx.beginPath();
        ctx.strokeStyle = `rgba(${70 + errorIntensity * 185}, 118, 154, 0.8)`;
        ctx.lineWidth = 2;
        ctx.moveTo(i, y1);
        ctx.lineTo(i + 4, y1);
        ctx.stroke();

        ctx.beginPath();
        ctx.strokeStyle = `rgba(124, ${181 - errorIntensity * 100}, 78, 0.8)`;
        ctx.moveTo(i, y2);
        ctx.lineTo(i + 4, y2);
        ctx.stroke();

        // Draw connecting lines
        if (i % 20 === 0 && i > 0) {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(142, 142, 144, ${0.7 - errorIntensity * 0.3})`;
          ctx.lineWidth = 1;
          ctx.moveTo(i, y1);
          ctx.lineTo(i, y2);
          ctx.stroke();
        }
      }

      frame++;
      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} width={400} height={200} />;
};
